import { useEffect, useMemo } from 'react';

interface PageHelmetProps {
  title?: string;
  description?: string;
  image?: string;
  keywords?: string[];
  canonicalUrl?: string;
  ogType?: string;
}

function PageHelmet({
  title,
  description,
  image,
  keywords,
  canonicalUrl,
  ogType = 'website',
}: PageHelmetProps) {
  const siteName = 'FinanSafe';
  const baseUrl = 'https://finansafe.com';

  const metaTitle = title ? `${title} | ${siteName}` : siteName;
  const metaDescription =
    description ||
    'Planejamento financeiro pessoal e empresarial simplificado. Crie planos de negócio, gerencie renda extra e tome decisões financeiras inteligentes.';
  const metaImage = image || `${baseUrl}/finansafe.png`;
  const metaKeywords = useMemo(
    () =>
      keywords || [
        'planejamento financeiro',
        'plano de negócio',
        'renda extra',
        'gestão financeira',
        'investimentos',
        'finanças pessoais',
        'FinanSafe',
      ],
    [keywords]
  );
  const metaCanonical =
    canonicalUrl ||
    (typeof window !== 'undefined' ? window.location.href : baseUrl);

  // Set meta tags and title using useEffect
  useEffect(() => {
    // Set document title
    document.title = metaTitle;

    // Helper function to set or update meta tag
    const setMetaTag = (name: string, content: string, property = false) => {
      const selector = property
        ? `meta[property="${name}"]`
        : `meta[name="${name}"]`;
      let element = document.querySelector(selector) as HTMLMetaElement;

      if (!element) {
        element = document.createElement('meta');
        if (property) {
          element.setAttribute('property', name);
        } else {
          element.setAttribute('name', name);
        }
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Helper function to set or update link tag
    const setLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(
        `link[rel="${rel}"]`
      ) as HTMLLinkElement;

      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      element.setAttribute('href', href);
    };

    // Set basic meta tags
    setMetaTag('description', metaDescription);
    setMetaTag('keywords', metaKeywords.join(', '));
    setMetaTag('robots', 'index, follow');
    setMetaTag('language', 'pt-BR');
    setMetaTag('author', 'FinanSafe');

    // Set Open Graph tags
    setMetaTag('og:type', ogType, true);
    setMetaTag('og:url', metaCanonical, true);
    setMetaTag('og:title', metaTitle, true);
    setMetaTag('og:description', metaDescription, true);
    setMetaTag('og:image', metaImage, true);
    setMetaTag('og:site_name', siteName, true);

    // Set Twitter tags
    setMetaTag('twitter:card', 'summary_large_image', true);
    setMetaTag('twitter:url', metaCanonical, true);
    setMetaTag('twitter:title', metaTitle, true);
    setMetaTag('twitter:description', metaDescription, true);
    setMetaTag('twitter:image', metaImage, true);

    // Set canonical link
    setLinkTag('canonical', metaCanonical);

    // Set content language
    document.documentElement.setAttribute('lang', 'pt-BR');
  }, [
    metaTitle,
    metaDescription,
    metaImage,
    metaKeywords,
    metaCanonical,
    ogType,
    siteName,
  ]);

  return null; // This component doesn't render anything
}

export default PageHelmet;
